import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { COPYRIGHT_STREAM_FRAGMENT } from '@optools/services/operator-gateway/btc/fragments';
import {
  DELETE_COPYRIGHT_STREAM,
  START_COPYRIGHT_STREAM,
  STOP_COPYRIGHT_STREAM,
} from '@optools/services/operator-gateway/btc/mutations';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { toast } from 'sonner';

import { CopyrightStreamStatus } from './types';

export interface CopyrightStreamActionsProps {
  status: CopyrightStreamStatus;
  streamId: string;
}

export function CopyrightStreamActions({ status, streamId }: CopyrightStreamActionsProps) {
  const { btcTools } = useAppSelector(selectToolsConfig);
  const [startStream, startStreamStatus] = useMutation(START_COPYRIGHT_STREAM, {
    onCompleted: ({ startCopyrightStream }) => {
      if (!startCopyrightStream) {
        toast.error('Start Stream Error', {
          description: 'Failed to start the Copyright Stream. Please try again.',
          duration: Infinity,
        });
      }
    },
    update: (cache, { data }) => {
      if (data?.startCopyrightStream) {
        cache.writeFragment({
          data: data.startCopyrightStream,
          fragment: COPYRIGHT_STREAM_FRAGMENT,
          id: cache.identify(data.startCopyrightStream),
        });
      }
    },
    variables: { streamId },
  });

  const [stopStream, stopStreamStatus] = useMutation(STOP_COPYRIGHT_STREAM, {
    onCompleted: ({ stopCopyrightStream }) => {
      if (!stopCopyrightStream) {
        toast.error('Stop Stream Error', {
          description: 'Failed to stop the Copyright Stream. Please try again.',
          duration: Infinity,
        });
      }
    },
    update: (cache, { data }) => {
      if (data?.stopCopyrightStream) {
        cache.writeFragment({
          data: data.stopCopyrightStream,
          fragment: COPYRIGHT_STREAM_FRAGMENT,
          id: cache.identify(data.stopCopyrightStream),
        });
      }
    },
    variables: { streamId },
  });

  const [deleteStream, deleteStreamStatus] = useMutation(DELETE_COPYRIGHT_STREAM, {
    onCompleted: ({ deleteCopyrightStream }) => {
      if (!deleteCopyrightStream) {
        toast.error('Delete Stream Error', {
          description: 'Failed to delete the Copyright Stream. Please try again.',
          duration: Infinity,
        });
      }
    },
    update: (cache, { data }) => {
      if (data?.deleteCopyrightStream) {
        cache.evict({ id: cache.identify({ __typename: 'CopyrightStream', id: streamId }) });
        cache.gc();
      }
    },
    variables: { streamId },
  });

  const showDeleteButton =
    btcTools.pages.schedule.deleteCopyrightStreamEnabled &&
    (status === 'notRecording' || status === 'recorded');

  return (
    <div className="flex items-center gap-2 pr-2">
      {status === 'notRecording' && (
        <ConfirmationDialog
          description="Are you sure you want to start the Copyright Stream?"
          title="Start Copyright Stream"
          onConfirm={() => startStream()}>
          <Button disabled={startStreamStatus.loading} loading={startStreamStatus.loading}>
            Start Stream
          </Button>
        </ConfirmationDialog>
      )}

      {status === 'recording' && (
        <ConfirmationDialog
          description="Are you sure you want to stop the Copyright Stream?"
          title="Stop Copyright Stream"
          onConfirm={() => stopStream()}>
          <Button disabled={stopStreamStatus.loading} loading={stopStreamStatus.loading}>
            Stop Stream
          </Button>
        </ConfirmationDialog>
      )}

      {showDeleteButton && (
        <ConfirmationDialog
          description="Are you sure you want to delete the Copyright Stream?"
          title="Delete Copyright Stream"
          onConfirm={() => deleteStream()}>
          <Button
            disabled={deleteStreamStatus.loading}
            loading={deleteStreamStatus.loading}
            variant="destructive">
            Delete Stream
          </Button>
        </ConfirmationDialog>
      )}
    </div>
  );
}
